import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ImportDataStage } from '..';
import InfoIconRed from '../../../../../../assets/img/info-icon-red.svg';
import ConfirmDialog from '../../../../../../dialogs/ConfirmDialog';
import Button from '../../../../../../react-components/Button';
import Text from '../../../../../../react-components/Text';
import { intercom } from '../../../../../../services/intercom';
import { FieldDefinition } from '../fields/FieldDefinitions';
import { FieldMapping, MappingItem } from '../fields/FieldMapping';
import { MapFieldsColumn } from './MapFieldsColumn';

export const MapFields = ({
  id,
  rawData,
  fields,
  externalIdMappingField,
  tasksEnabled,
  addressFields,
  onCompleted,
  onBack,
}: {
  id: string;
  rawData: any[][];
  fields: FieldDefinition[];
  externalIdMappingField?: MappingItem;
  tasksEnabled?: boolean;
  addressFields?: FieldDefinition[];
  onCompleted: (mappedData: FieldMapping, skipTasks: boolean) => void;
  onBack: () => void;
}) => {
  const intl = useIntl();
  const headers = rawData[0];
  const data = rawData.slice(1);

  const [mapping, setMapping] = useState<FieldMapping>(new FieldMapping([], data));
  const [showSkipTasks, setShowSkipTasks] = useState<boolean>(false);

  // This code is used to load the mapping from local storage on first render
  const idRef = useRef(id);
  const fieldsRef = useRef(FieldMapping.sortFields(fields, addressFields));
  const headersRef = useRef(headers);
  const dataRef = useRef(data);

  // The translation between the possible ID's and the translation ID's
  const continueIntlIdDict = {
    map_stop_fields: `plans.dialogs.upload_stops.map_stop_fields`,
    map_task_fields: `plans.dialogs.upload_stops.map_task_fields`,
    map_pickup_fields: `plans.dialogs.upload_stops.map_pickup_fields`,
  } as {
    [key in ImportDataStage]: string;
  };

  useEffect(() => {
    const savedMapping = localStorage.getItem(idRef.current);
    if (savedMapping) {
      const mapping = FieldMapping.fromStorage(
        savedMapping,
        fieldsRef.current,
        headersRef.current.map((value, idx) => ({ index: idx, name: value })),
        dataRef.current
      );
      if (externalIdMappingField) {
        mapping.add(externalIdMappingField.field, externalIdMappingField.column);
      }
      setMapping(mapping);
    } else {
      const newMapping = new FieldMapping([], dataRef.current);
      if (externalIdMappingField) {
        newMapping.add(externalIdMappingField.field, externalIdMappingField.column);
      }
      setMapping(newMapping);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array means this effect runs once on mount

  const getMappedDataLength = () => {
    if (!tasksEnabled || !mapping.getById(`external_id`)) {
      return data.length;
    } else {
      return Object.keys(
        mapping.getData().reduce((acc, row) => {
          if (row.external_id) {
            acc[row.external_id] = true;
          }
          return acc;
        }, {} as { id: number; [value: string]: any })
      ).length;
    }
  };

  return (
    <>
      <Grid container direction="column" wrap="nowrap" style={{ height: `calc(80vh - 52px)` }}>
        <Grid container direction="row" justifyContent="space-between" wrap="nowrap">
          <Grid item container direction="row">
            <Text intlId="plans.dialogs.upload_stops.map_fields.rows" intlValues={{ rows: getMappedDataLength() }} />
            <HSpacer small />
            <Text intlId="plans.dialogs.upload_stops.map_fields.columns" intlValues={{ columns: headers.length }} />
            <HSpacer small />
            <Text
              intlId="plans.dialogs.upload_stops.map_fields.mapped_fields"
              intlValues={{ fields: mapping.getItems().length }}
            />
          </Grid>
        </Grid>

        <VSpacer medium />

        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{
            height: `calc(100% - 80px)`,
            border: `1px solid ${RoutalPalette.grey4}`,
            borderRadius: `10px`,
            padding: `20px`,
            overflow: `auto`,
          }}
        >
          {headers.map((header, headerIndex) => (
            <MapFieldsColumn
              key={headerIndex}
              columnName={header}
              columnIndex={headerIndex}
              isLastColumn={headerIndex === headers.length - 1}
              mapping={mapping}
              fields={fields}
              addressFields={addressFields}
              setMapping={setMapping}
            />
          ))}
        </Grid>

        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          style={{ height: `80px` }}
        >
          <Button
            variant="flat"
            styleType="secondary"
            style={{ minWidth: `80px` }}
            onClick={() => onBack()}
            intlId="back"
          />

          <Grid
            item
            container
            direction="row"
            justifyContent="flex-end"
            flexWrap="nowrap"
            padding={`0px 10px`}
            style={{ width: `unset` }}
          >
            {addressFields && mapping.hasAnyAddressField() ? (
              <>
                {mapping.hasValidAddress() ? (
                  <>
                    <Text style={{ color: RoutalPalette.primary40 }}>
                      {`${intl.formatMessage({
                        id: `plans.dialogs.upload_stops.map_fields.location_mapping_example`,
                      })}:`}
                    </Text>

                    <HSpacer small />

                    <Text>{mapping.getAddresses().slice(0, 1)}</Text>
                  </>
                ) : (
                  <>
                    <Text
                      style={{ color: RoutalPalette.danger }}
                      intlId="plans.dialogs.upload_stops.map_fields.location_mapping_example.invalid"
                    />

                    <HSpacer small />

                    <img
                      style={{ cursor: `pointer` }}
                      onClick={() => intercom.open()}
                      alt=""
                      src={InfoIconRed}
                      width="16px"
                      draggable="false"
                    />
                  </>
                )}
              </>
            ) : null}
          </Grid>

          <Button
            intlId={continueIntlIdDict[id as ImportDataStage] ?? `continue`}
            style={{ minWidth: `80px` }}
            onClick={() => {
              if (tasksEnabled && !mapping.getById(`external_id`)) {
                setShowSkipTasks(true);
              } else {
                localStorage.setItem(id, FieldMapping.toStorage(mapping));
                onCompleted(mapping, false);
              }
            }}
          />
        </Grid>
      </Grid>

      <ConfirmDialog
        title={<Text variant="h5" intlId="plans.dialogs.upload_stops.map_fields.skip_tasks_dialog.title" />}
        description={<Text intlId="plans.dialogs.upload_stops.map_fields.skip_tasks_dialog.content" />}
        open={showSkipTasks}
        onCloseDialog={() => setShowSkipTasks(false)}
        onConfirm={() => {
          localStorage.setItem(id, FieldMapping.toStorage(mapping));
          onCompleted(mapping, true);
        }}
      />
    </>
  );
};
